// Firebase Notification System - Fixed
// This is a complete implementation using Firebase Cloud Messaging (FCM)

// 1. FIREBASE CONFIGURATION
// firebase-config.js
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { messaging } from "./FirebaseConfig";
import { getCurrentUser, getOrRefreshFirebaseToken } from "./getFirebaseToken";
import { getDocDataById } from "../models/DataFunctions";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import { Alert, Modal } from "antd";

const vapidKey =
  "BIa1I34J_xP4d8_2yqMXs7hMbuNJ7DRCCLH1Htdcfihr2dqfrp2LFHLMIJDww4d6G_BmEaB9cuSadTUtErnnwbk";

// Attach the foreground onMessage listener at most once per page load, even if
// several NotificationManager instances initialize (e.g. the pop-up toggle is
// flipped repeatedly) — otherwise each instance would show a duplicate pop-up.
let foregroundListenerAttached = false;

// 2. CLIENT-SIDE NOTIFICATION HANDLER
class NotificationManager {
  constructor() {
    this.token = null;
    this.isSupported = "serviceWorker" in navigator && "PushManager" in window;
    this.serviceWorkerRegistration = null;
  }

  async initialize(user_id, prefix, feedback = true) {
    if (!this.isSupported) {
      throw new Error("Push notifications are not supported");
    }

    // Register service worker and store registration
    this.serviceWorkerRegistration = await this.registerServiceWorker();
    console.log("Permission for notifications:", Notification.permission);
    if (Notification.permission !== "granted") {
      permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }
    }

    // Get FCM token
    this.token = await this.getFCMToken(user_id, prefix, feedback);

    // Listen for foreground messages
    this.setupForegroundListener();

    return this.token;
  }

  async registerServiceWorker() {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    console.log("Service Worker registered:", registration);
    return registration;
  }

  async requestPermission() {
    const permission = await Notification.requestPermission();
    console.log("Notification permission:", permission);
    return permission;
  }

  async getFCMToken(user_id, prefix, feedback = true) {
    try {
      console.log("Requesting FCM token...");
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
      });
      console.log("FCM token received:", token);

      let user = await getDocDataById("user", user_id);
      let fcm_tokens = user?.fcm_tokens || [];
      console.log("Existing tokens:", fcm_tokens);

      if (token) {
        if (!fcm_tokens.map((item) => item.value).includes(token)) {
          // Send this token to your server to store for the user
          console.log("Sending token to server...");
          await this.sendTokenToServer(prefix, token, user_id);
          return token;
        } else {
          console.log("Token already registered with server.");
          // open a dialog to inform user
          if (feedback) {
            Modal.info({
              title: "Notifications déjà activées",
              content:
                "Vous avez déjà activé les notifications pour cet appareil.",
              okText: "OK",
              onOk() {},
            });
          }
        }
      } else {
        console.log("No registration token available");
        return token;
      }
    } catch (err) {
      console.error("An error occurred while retrieving token:", err);
      throw err;
    }
  }

  setupForegroundListener() {
    if (foregroundListenerAttached) return;
    foregroundListenerAttached = true;
    onMessage(messaging, (payload) => {
      console.log("Message received in foreground:", payload);

      // Display notification using service worker for full feature support
      this.displayServiceWorkerNotification(payload.notification, payload.data);
    });
  }

  // Use service worker for rich notifications with actions
  async displayServiceWorkerNotification(notification, data = {}) {
    if (!notification || !this.serviceWorkerRegistration) return;

    const notificationTitle = notification.title || "New Message";
    const notificationOptions = {
      body: notification.body || "You have a new message",
      icon: notification.icon || "/icon.png",
      badge: "/icon.png",
      tag: "message-notification",
      requireInteraction: true,
      actions: [
        {
          action: "open",
          title: "Open",
          icon: "/open-icon.png",
        },
        {
          action: "close",
          title: "Close",
          icon: "/close-icon.png",
        },
      ],
      data: {
        ...data,
        url: window.location.origin,
        timestamp: Date.now(),
      },
    };

    try {
      await this.serviceWorkerRegistration.showNotification(
        notificationTitle,
        notificationOptions
      );
    } catch (error) {
      console.error("Error showing service worker notification:", error);
      // Fallback to simple notification
      this.displaySimpleNotification(notification);
    }
  }

  // Fallback simple notification without actions
  displaySimpleNotification(notification) {
    if (!notification) return;

    const notificationTitle = notification.title || "New Message";
    const notificationOptions = {
      body: notification.body || "You have a new message",
      icon: notification.icon || "/icon.png",
      badge: "/icon.png",
      tag: "message-notification",
      requireInteraction: true,
      // Remove actions for simple notifications
      data: notification.data || {},
    };

    new Notification(notificationTitle, notificationOptions);
  }

  // --- Pop-up notifications toggle helpers (web) ---------------------------
  // The messagerie pop-up toggle is backed by FCM-token presence: a registered
  // token means the backend pushes (so the service worker shows a system pop-up
  // even when the user is on another tab); removing it stops the pop-ups.

  // Get this browser's FCM token WITHOUT registering it on the server. Returns
  // null when unsupported or when notification permission isn't granted.
  async getDeviceToken() {
    if (!this.isSupported) return null;
    if (Notification.permission !== "granted") return null;
    try {
      if (!this.serviceWorkerRegistration) {
        this.serviceWorkerRegistration = await this.registerServiceWorker();
      }
      return await getToken(messaging, { vapidKey });
    } catch (e) {
      console.warn("getDeviceToken failed:", e);
      return null;
    }
  }

  // True if this device currently has its FCM token registered for the user
  // (i.e. pop-ups are on for this browser).
  async isEnabledForDevice(user_id) {
    const token = await this.getDeviceToken();
    if (!token) return false;
    try {
      const user = await getDocDataById("user", user_id);
      return (user?.fcm_tokens || []).some((t) => t.value === token);
    } catch {
      return false;
    }
  }

  // Turn pop-ups off for THIS browser by removing its token from the user doc.
  async disableForDevice(user_id, prefix) {
    const token = await this.getDeviceToken();
    const user = await getDocDataById("user", user_id);
    if (!user || !user.ref) return;
    // Remove this device's token; if we couldn't resolve it (e.g. permission
    // since revoked), fall back to dropping all web tokens so the toggle still
    // reliably stops pop-ups.
    const tokens = user.fcm_tokens || [];
    const remaining = token
      ? tokens.filter((t) => t.value !== token)
      : tokens.filter((t) => t.platform && t.platform !== "web");
    await updateDocDataFieldApi(user.ref, "fcm_tokens", remaining, prefix);
  }

  // Remove ONLY this browser's token from the user doc on logout / account
  // switch, so the backend stops pushing the left account's message
  // notifications here (system pop-ups via the service worker would otherwise
  // keep arriving for conversations the user no longer has access to). Unlike
  // disableForDevice (the pop-up toggle), this never falls back to dropping all
  // web tokens: if we can't identify this device (no permission / unsupported),
  // we leave the doc untouched rather than knocking out the user's other
  // browsers.
  async removeThisDeviceToken(user_id, prefix) {
    try {
      const token = await this.getDeviceToken();
      if (!token || !user_id) return;
      await fetch(`${prefix}fcm/delete-fcm-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await this.getAuthToken()}`,
        },
        body: JSON.stringify({ token, userId: user_id }),
      });
    } catch (e) {
      console.warn("removeThisDeviceToken failed:", e);
    }
  }

  async sendTokenToServer(prefix, token, user_id) {
    try {
      await fetch(prefix + "fcm/save-fcm-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          token: token,
          userId: user_id,
        }),
      });
    } catch (error) {
      console.error("Error sending token to server:", error);
    }
  }

  async getAuthToken() {
    // Return your app's authentication token
    return await getOrRefreshFirebaseToken();
  }
}

// 4. USAGE EXAMPLE
class ChatApp {
  constructor() {
    this.notificationManager = new NotificationManager();
  }

  async init(user_id, prefix, feedback = true) {
    try {
      // Initialize notifications
      const token = await this.notificationManager.initialize(
        user_id,
        prefix,
        feedback
      );
      console.log("Notification system initialized with token:", token);

      // Subscribe to relevant topics
      // await this.subscribeToTopics(["general", "announcements"]);
    } catch (error) {
      console.error("Failed to initialize notifications:", error);
    }
  }

  async sendMessage(
    recipientId,
    sender_id,
    title,
    message,
    folder_id,
    prefix,
    workspaceData
  ) {
    try {
      // Send message through your chat system
      await this.sendChatMessage(recipientId, message);

      // Send push notification
      await fetch(prefix + "fcm/send-notification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: recipientId,
          title: title,
          body: message.substring(0, 100),
          data: {
            messageId: Date.now().toString(),
            senderId: sender_id,
            type: "chat_message",
            folder_id: folder_id,
            route: workspaceData
              ? `/workspace/${workspaceData.id}/messages?folder_id=${folder_id}`
              : `/messages?folder_id=${folder_id}`,
          },
        }),
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }

  sendChatMessage(recipientId, message) {
    // Your existing chat message logic
    return Promise.resolve();
  }
}

export { NotificationManager, ChatApp };
